import { createSelector } from 'reselect'
import memoize from 'memoizerific'

import {
  chain, minBy, size, every, pick, pickBy, sortBy, values, 
  compact, mapValues, keys, uniq, set, findKey
} from 'lodash'

import { sorted, indexBy } from '../../lib/Tools'
import { allCountries, mainCountries, otherCountries } from '../../lib/countries.js'

import { translateCity } from '../lib/text'

import { getIdFromUrl } from './auxiliary'
import { getTrips } from './trips'
import { isRestaurant } from '../tags/TagsIds'

export * from './trips'

/**
 * general
 */

export const getQuery = (_state, props) => props.location.query || null
export const getFromQuery = (_state, props) => key => props.location.query[key] || null
export const getFromQuery2 = key => (_state, props) => props.location.query[key] || null
export const getHash = (_state, props) => props.location.hash

// export const getReadyState = (state, props) => (
//   every(values(pick(state, ["users", "blocks", "cities", "routes", "activities", "trips", "suppliers", "loginData"])).map(x => x.length > 0))
// )

export const getReadyState = (state, propsIgnored) => {
  const rs = pick(state, ["users", "blocks", "cities", "routes", "activities", "trips", "suppliers", "loginData"])
  const lengths = values(rs).map(docState => keys(docState).length)

  return every(lengths.map(x => x > 0))
}

/**
 * tags
 */

const _createTagsSelector = (tagType) => (
  createSelector(
    [state => state.tags],
    tags => pickBy(tags, tag => tag.tagType === tagType)
  )
)

export const getTripTags = _createTagsSelector("trips")
export const getActivityTags = _createTagsSelector("activities")
export const getProductTags = _createTagsSelector("products")

export const getTagsForType = createSelector(
  [
    (state => state.tags), 
    ((_, props) => props.tagType)
  ],
  (tags, type) => pickBy(tags, t => t.tagType === type)
)

export const getNoPubliTagId = createSelector(
  state => state.tags,
  tags => findKey(tags, { name: "NO_PUBLI" })
)

/**
 * reservations
 */

export const getReservations = (state, _props) => state.reservations

export const getTripFromProps = (_state, props) => props.trip

export const getTripReservations = createSelector(
  [getReservations, getTripFromProps],
  (R, t) => pickBy(R, r => r.tripId == t._id)
)


/**
 * activities
 */

export const getAllActivities = (state, _props) => state.activities

export const getActivities = createSelector(
  [getAllActivities],
  A => {
    const r = (!A ? {} : pickBy(A, a => !a.deleted))
    return r
  }
)

export const getDeletedActivities = createSelector(
  [getAllActivities],
  A => {
    const r = (!A ? {} : pickBy(A, a => a.deleted))
    return r
  }
)

export const getActivitiesList = createSelector(
  [getActivities], activities => values(activities)
)

export const getCityActivities = memoize(3)((state, cityId) => (
  sortBy(
    getActivitiesList(state).filter(a => a.city == cityId),
    "name"
  )
))

export const getActivityForUrl = createSelector(
  [getIdFromUrl, getAllActivities],
  (id, A) => A[id]
)

export const getActivitiesByCity = createSelector(
  [getActivities],
  A => chain(A).values().groupBy("city").value()
)

/**
 * suppliers
 */
export const getAllSuppliers = (state, _props) => state.suppliers

export const getSuppliers = createSelector(
  [getAllSuppliers],
  S => {
    const r = (!S ? {} : pickBy(S, s => !s.deleted))
    return r
  }
)

export const getSuppliersList = createSelector(
  [getSuppliers], suppliers => values(suppliers)
)

export const getSupplierForUrl = createSelector(
  [getIdFromUrl, getAllSuppliers],
  (id, S) => S[id]
)
export const getActivitiesPerSupplier = createSelector(
  [getSuppliersList, getActivitiesList],
  (suppliersList, activities) => {
    const activitiesPerSupplier = {}
    suppliersList.forEach(supplier => {
      activitiesPerSupplier[supplier._id] = activities.filter(a => a.supplier == supplier._id)
    })
    return activitiesPerSupplier
  }
)

/**
 * templates / blocks
 */
const getAllBlocks = (state, _props) => state.blocks

// TODO shouldnt be necesary to check block.template, but non-template blocks are still in the db
export const getBlocks = createSelector(
  [getAllBlocks],
  (allBlocks) => (
    !allBlocks ? {} : pickBy(allBlocks, b => b.template && !b.deleted)
  )
)

export const getBlocksList = createSelector(
  [getBlocks], B => values(B)
)

export const getBlockForUrl = createSelector(
  [getIdFromUrl, getBlocks], (id, B) => B[id]
)

/**
 * cities
 * there are no deleted cities
 */
export const getAllCities = (state, _props) => state.cities

export const getCities = createSelector(
  [getAllCities],
  allCities => allCities
)

export const getCitiesIdList = createSelector(
  [getAllCities],
  allCities => sorted(keys(allCities),
    (a, b) => a.localeCompare(b))
)

export const getCitiesByCountry = createSelector(
  [getAllCities],
  cities => indexBy(values(cities), "country", (a, b) => a.name.localeCompare(b.name))
)

export const getCountriesWithCities = createSelector(
  [getAllCities],
  cities => {
    const countries = new Set(values(cities).map(x => x.country))
    return {
      allCountries: pickBy(allCountries, c => countries.has(c.name)),
      mainCountries: mainCountries.filter(c => countries.has(c.name)),
      otherCountries: otherCountries.filter(c => countries.has(c.name)),
    }
  }
)


/**
 * products
 */

export const getAllProducts = (state, _props) => state.products

export const getProducts = createSelector(
  [getAllProducts],
  products => pickBy(products, p => !p.deleted)
)

export const getProductForUrl = createSelector(
  [getIdFromUrl, getProducts],
  (id, A) => A[id]
)

export const getOrganizedProducts = createSelector(
  [getProducts],
  (products) => {
    const organizedProducts = {}
    values(products).forEach(product => {
      set(organizedProducts, [product.city, isRestaurant(product) ? "restaurant" : "other", product._id], product)
    })
    return organizedProducts
  }
)

export const getProductsByType = createSelector(
  [getProducts],
  (products) => {
    const typizedProducts = {}
    values(products).forEach(product => {
      set(typizedProducts, [isRestaurant(product) ? "restaurant" : "other", product._id], product)
    })
    return typizedProducts
  }
)
export const getProductsByCity = createSelector(
  [getProducts],
  (products) => {
    const cityzedProducts = {}
    values(products).forEach(product => {
      set(cityzedProducts, [product.city, product._id], product)
    })
    return cityzedProducts
  }
)

export const getCitiesWithProducts = createSelector(
  [getProducts],
  (products) => {
    const goodCities = []
    values(products).forEach(product => {
      goodCities.push(product.city)
    })
    return uniq(goodCities)
  }
)

// #TODO remove this shit
export const makeGetCityLabel = createSelector(
  [getAllCities],
  cities => id => {
    const city = cities[id]
    if (!city) return `⚠️ No existe: ${id}`
    return translateCity(city)
  }
)
// #TODO remove this shit
export const makeGetCityCountry = createSelector(
  [getAllCities],
  cities => id => {
    const city = cities[id]
    if (!city) return null

    const country = allCountries[city.country]
    return country
  }
)

/**
 * expenses
 * there are no deleted expense
 */
const getAllExpenses = (state, _props) => state.expenses

export const getExpenses = getAllExpenses

export const getSortedExpenses = createSelector(
  [getAllExpenses],
  expenses => sortBy(values(expenses || {}), ["city", "name", "title"])
)

/**
 * users
 */
const getAllUsers = (state, _props) => state.users
export const getThisUsername = (state, _props) => state.loginData.username
export const isLoggedIn = (state, _props) => !!state.loginData.username

export const getUsernames = createSelector(
  [getAllUsers],
  users => keys(users)
)

export const getActiveUsernames = createSelector(
  [getAllUsers],
  U => {
    const r = (!U ? {} : pickBy(U, u => u.active))
    return new Set(keys(r))
  }
)
/**
 * tasks
 */

export const getAllTasks = (state, _props) => state.tasks
const getThisTripId = (_state, props) => props.trip._id

export const getPendingTasks = createSelector([getAllTasks], T => pickBy(T, t => t.status == "pending"))

export const getThisUserPendingTasks = createSelector(
  [getThisUsername, getPendingTasks, getTrips],
  (u, Tasks, Trips) => (
    sortBy(
      // filter user's tasks: they are not link to a trip, or if they are, the trip exists
      pickBy(Tasks, task => task.user == u && (!task.trip || Trips[task.trip])),
      "dueDate"
    )
  )
)

export const makeGetUserTasks = createSelector(
  [getPendingTasks, getTrips],
  (Tasks, Trips) => u => (
    sortBy(
      // filter user's tasks: they are not link to a trip, or if they are, the trip exists
      pickBy(Tasks, task => task.user == u && (!task.trip || Trips[task.trip])),
      "dueDate"
    )
  )
)

/**
 * Warning: nested memoization ahead
 * this produces a memoized function. whenever the tasks change, the memoized
 * function will be created again (equivalent to reseting the cache)
 * this is a nasty hack, i know that. i'm researching a better
 * memoization strategy.
 */
export const makeGetTripTasks = createSelector(
  [getAllTasks],
  tasks => (
    memoize(500)(tripId => (
      sortBy(
        values(pickBy(tasks, t => t.trip === tripId)),
        t => `${t.status == "pending" ? "A" : "B"}.${t.dueDate || "Infinity"}`
      )
    ), { length: 1 })
  )
)

export const makeGetTripPendingTasks = createSelector(
  [getPendingTasks],
  tasks => (
    memoize(1000)(tripId => (
      sortBy(
        values(pickBy(tasks, t => t.trip === tripId)),
        t => `${t.dueDate || "Infinity"}`
      )
    ), { length: 1 })
  )
)

export const getThisTripTasks = createSelector(
  [getThisTripId, getAllTasks],
  (tripId, tasks) => (
    sortBy(
      values(tasks).filter(t => t.trip === tripId),
      t => `${t.status == "pending" ? "A" : "B"}.${t.dueDate || "Infinity"}`
    )
  )
)

// per trip, per user, their next task
// expects pendingTasks to come ordered!
export const getNextTaskPerTrip = createSelector(
  [getTrips, getPendingTasks, makeGetTripPendingTasks],
  (trips, tasks, get) => (
    mapValues(trips, trip => (
      chain(get(trip._id))
        .groupBy(t => t.user)
        .mapValues(userTasks => minBy(userTasks, t => "dueDate" || "9999/01/01"))
        .value()
    ))
  )
)

export const getTasksCountPerTrip = createSelector(
  [getTrips, getPendingTasks, makeGetTripPendingTasks],
  (trips, tasks, get) => (
    mapValues(trips, trip => {
      const pendingTasks = get(trip._id)
      return size(pendingTasks)
    })
  )
)

/**
 * routes
 */

export const getAllRoutes = (state, _props) => state.routes

export const getRouteForBlock = createSelector(
  [getAllRoutes, getBlockForUrl],
  (R, b) => (b && b.route ? R[b.route] : null)
)

const getCitiesInRoute = memoize(3)(route => new Set(compact(route.agenda.map(x => x.city))))

export const makeGetBlockCities = createSelector(
  [getBlocks, getAllRoutes], (B, R) => {
    const blockCitiesMap = mapValues(B, b => getCitiesInRoute(R[b.route]))
    return id => blockCitiesMap[id]
  }
)
